import type {
  LoginRequest,
  LoginResponse,
  LoginResult,
  LoginVerifyRequest,
  Member,
  Round,
  Game,
  Bet,
  PlaceBetRequest,
  PlaceBetResponse,
  Wallet,
  Settlement,
  WalletTransaction,
  WinLossReport,
  MasterWinLossReport,
  MemberBetSummary,
  AgentBetItem,
  PaginatedResult,
  FetchedPrice,
  DrawLottery,
  DrawLotteryWithCurrent,
  DrawLotteryRules,
  DrawLotteryRuleImage,
  DrawScheduleConfig,
  CreateDrawLotteryRequest,
  OtpChallengeIssued,
  OtpConfirmation,
  DrawBetType,
  DrawBetTypeConfig,
  DrawStatus,
  DrawBlockedNumber,
  DrawNumberCap,
  DrawNumberCapInput,
  AgentNumberRule,
  AgentNumberRuleInput,
  Draw,
  DrawWithResult,
  Banner,
  BannerInput,
  ApiError,
  StagingTarget,
  StagingCaseMeta,
  StagingRunDetail,
  StagingRunSummary,
  CreateStagingRunRequest,
  DrawAutoOpenSettings,
  NumberLimitHistoryActors,
  NumberLimitHistoryEntry,
  NumberLimitHistoryQuery,
} from '@nexbit/types'

const API_BASE = '/api'
const TOKEN_KEY = 'nexbit_admin_token'

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = sessionStorage.getItem(TOKEN_KEY)
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init.headers as Record<string, string> | undefined),
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })

  if (!res.ok) {
    let errorBody: ApiError = { code: 'UNKNOWN', message: res.statusText }
    try {
      errorBody = (await res.json()) as ApiError
    } catch {
      // ignore parse errors
    }
    throw new ApiRequestError(res.status, errorBody.code, errorBody.message, errorBody.details)
  }

  if (res.status === 204) return undefined as unknown as T
  return res.json() as Promise<T>
}

/**
 * Fetch a file endpoint with auth and trigger a browser download. Kept separate
 * from `request` because the response is a binary Blob, not JSON, and the auth
 * header rules out a plain <a download> link.
 */
async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const token = sessionStorage.getItem(TOKEN_KEY)
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    let errorBody: ApiError = { code: 'UNKNOWN', message: res.statusText }
    try { errorBody = (await res.json()) as ApiError } catch { /* not JSON */ }
    throw new ApiRequestError(res.status, errorBody.code, errorBody.message, errorBody.details)
  }
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^"]+)"?/.exec(disposition)
  const filename = match?.[1] ?? fallbackName
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

/**
 * Sign in. An admin account does not get a session here: the answer is a challenge,
 * and the code that finishes it is in the operators' Telegram group. Members and
 * agents get their tokens straight away, as before — use isLoginOtpRequired to tell
 * the two answers apart.
 */
export async function apiLogin(body: LoginRequest): Promise<LoginResult> {
  return request<LoginResult>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** Finish an admin sign-in with the code from the group. */
export async function apiLoginVerify(body: LoginVerifyRequest): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/login/verify', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function apiRefreshToken(refreshToken: string): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  })
}

export async function apiLogout(): Promise<void> {
  return request<void>('/auth/logout', { method: 'POST' })
}

export async function apiMe(): Promise<{ member: Member; role: string }> {
  return request<{ member: Member; role: string }>('/auth/me')
}

// ─── Wallet (member-facing) ───────────────────────────────────────────────────

export async function apiGetWallet(): Promise<Wallet> {
  return request<Wallet>('/wallet')
}

export async function apiGetTransactions(page = 1, limit = 20): Promise<PaginatedResult<WalletTransaction>> {
  return request<PaginatedResult<WalletTransaction>>(`/wallet/transactions?page=${page}&limit=${limit}`)
}

// ─── Games & Rounds ───────────────────────────────────────────────────────────

export async function apiGetGames(): Promise<Game[]> {
  return request<Game[]>('/games')
}

export async function apiGetGame(gameId: string): Promise<Game> {
  return request<Game>(`/games/${gameId}`)
}

export async function apiGetCurrentRound(gameId: string): Promise<Round> {
  const data = await request<{ round: Round; game: Game }>(`/games/${gameId}/rounds/current`)
  return data.round
}

export async function apiGetRounds(gameId: string, page = 1, limit = 20): Promise<PaginatedResult<Round>> {
  return request<PaginatedResult<Round>>(`/games/${gameId}/rounds?page=${page}&limit=${limit}`)
}

export async function apiGetPrices(gameId: string): Promise<FetchedPrice[]> {
  return request<FetchedPrice[]>(`/games/${gameId}/prices`)
}

// ─── Bets (member-facing) ─────────────────────────────────────────────────────

export async function apiPlaceBet(body: PlaceBetRequest): Promise<PlaceBetResponse> {
  return request<PlaceBetResponse>('/bets', { method: 'POST', body: JSON.stringify(body) })
}

export async function apiGetMyBets(page = 1, limit = 20): Promise<PaginatedResult<Bet>> {
  return request<PaginatedResult<Bet>>(`/bets/mine?page=${page}&limit=${limit}`)
}

// ─── Admin — Partners ─────────────────────────────────────────────────────────

export interface AdminPartner {
  id: string
  code: string
  name: string
  parentId: string | null
  level: 'master' | 'agent'
  commissionRate: string
  masterShareRate: string | null
  status: string
  isActive: boolean
  walletType: string
  walletUrl: string | null
  nexbitApiKeyMasked: string
  launchSecretMasked: string
  /** '' when the agent has never set one — the raw value never round-trips over the API. */
  walletApiKeyMasked: string
  /** The agent's login username (master's agent page / create reply). null = no login account. */
  loginUsername?: string | null
  createdAt: string
  updatedAt: string
}

export interface CreatePartnerBody {
  code: string
  name: string
  commissionRate: string
  walletType: 'internal' | 'seamless'
  walletUrl?: string
  walletApiKey?: string
  level?: 'master' | 'agent'
  parentId?: string | null
  masterShareRate?: string
}

export interface UpdatePartnerBody {
  name: string
  commissionRate: string
  status: string
  isActive: boolean
  walletType: 'internal' | 'seamless'
  walletUrl?: string
  walletApiKey?: string
}

export async function apiAdminGetPartners(): Promise<AdminPartner[]> {
  return request<AdminPartner[]>('/admin/partners')
}

export async function apiAdminGetPartner(id: string): Promise<AdminPartner> {
  return request<AdminPartner>(`/admin/partners/${id}`)
}

// Partner mutations, key rotation, and admin-side member creation/impersonation were
// removed with the flat menus: admins manage masters (/admin/masters) and drill the
// hierarchy read-only; only the agent console creates members or impersonates them.

// ─── Admin — Dashboard ────────────────────────────────────────────────────────

export interface AdminDashboardStats {
  totalMembers: number
  activeMembers: number
  totalBetsToday: number
  totalBetAmountToday: string
  totalWinAmountToday: string
  openRounds: number
}

export async function apiAdminGetDashboard(): Promise<AdminDashboardStats> {
  return request<AdminDashboardStats>('/admin/dashboard')
}

// ─── Admin — Members ──────────────────────────────────────────────────────────

export interface AdminMember {
  id: string
  partnerId: string
  username: string
  displayName: string | null
  role: string
  status: string
  createdAt: string
  updatedAt: string
}

export interface AdminCreateMemberBody {
  username: string
  password: string
  displayName?: string
  role?: 'member' | 'admin' | 'partner'
  partnerId?: string
}

// ─── Admin — Games ────────────────────────────────────────────────────────────

export async function apiAdminGetGames(): Promise<Game[]> {
  return request<Game[]>('/admin/games')
}

export interface AdminUpdateGameBody {
  minBet: string
  maxBet: string
  roundDurationSeconds: number
  betCloseBeforeSeconds: number
  config: {
    payoutRates: { high: number; low: number; odd: number; even: number }
    winConditions: { highThreshold: number }
  }
  isActive: boolean
}

export async function apiAdminUpdateGame(id: string, body: AdminUpdateGameBody): Promise<Game> {
  return request<Game>(`/admin/games/${id}`, { method: 'PUT', body: JSON.stringify(body) })
}

// ─── Admin — Rounds ───────────────────────────────────────────────────────────

export async function apiAdminGetRounds(params: {
  page?: number; limit?: number; gameId?: string; status?: string
}): Promise<PaginatedResult<Round>> {
  const q = new URLSearchParams()
  if (params.page)   q.set('page',   String(params.page))
  if (params.limit)  q.set('limit',  String(params.limit))
  if (params.gameId) q.set('gameId', params.gameId)
  if (params.status) q.set('status', params.status)
  return request<PaginatedResult<Round>>(`/admin/rounds?${q.toString()}`)
}

// ─── Admin — Bets ─────────────────────────────────────────────────────────────

export interface AdminBet {
  id: string
  memberId: string
  memberUsername: string
  /** Which book the bet came from — only a price-game bet can be rolled back on its own. */
  kind: 'game' | 'draw'
  gameId: string
  gameCode: string
  /** Display name of the game or lottery. */
  productName: string
  roundId: string
  roundNumber: number
  /** งวด date for a lottery bet; null for a price game. */
  drawDate: string | null
  roundLabel: string | null
  betType: string
  /** The number staked on a lottery bet; null for a price game. */
  number: string | null
  amount: string
  payoutRate: string
  winAmount: string | null
  status: string
  partnerDebitTxId: string | null
  partnerCreditTxId: string | null
  createdAt: string
}

export async function apiAdminGetBets(params: {
  page?: number; limit?: number; memberId?: string; gameId?: string; status?: string
  roundId?: string; product?: string
}): Promise<PaginatedResult<AdminBet>> {
  const q = new URLSearchParams()
  if (params.page)     q.set('page',     String(params.page))
  if (params.limit)    q.set('limit',    String(params.limit))
  if (params.memberId) q.set('memberId', params.memberId)
  if (params.gameId)   q.set('gameId',   params.gameId)
  if (params.status)   q.set('status',   params.status)
  if (params.roundId)  q.set('roundId',  params.roundId)
  if (params.product)  q.set('product',  params.product)
  return request<PaginatedResult<AdminBet>>(`/admin/bets?${q.toString()}`)
}

/** Games and lotteries a bet could have been placed on — the filter's options. */
export interface AdminBetProduct { code: string; name: string; kind: 'game' | 'draw' }

export async function apiAdminGetBetProducts(): Promise<AdminBetProduct[]> {
  return request<AdminBetProduct[]>('/admin/bets/products')
}

export async function apiAdminRollbackBet(id: string, otp: OtpConfirmation): Promise<AdminBet> {
  return request<AdminBet>(`/admin/bets/${id}/rollback`, {
    method: 'POST',
    body: JSON.stringify({ otp }),
  })
}

// ─── Admin — Reports ──────────────────────────────────────────────────────────

export async function apiAdminGetWinLossReport(params: {
  from: string; to: string; partnerId?: string; masterId?: string
}): Promise<WinLossReport[]> {
  const q = new URLSearchParams({ from: params.from, to: params.to })
  if (params.partnerId) q.set('partnerId', params.partnerId)
  if (params.masterId) q.set('masterId', params.masterId)
  return request<WinLossReport[]>(`/admin/reports/winloss?${q.toString()}`)
}

export async function apiAdminExportWinLossReport(params: {
  from: string; to: string; partnerId?: string; masterId?: string
}): Promise<void> {
  const q = new URLSearchParams({ from: params.from, to: params.to })
  if (params.partnerId) q.set('partnerId', params.partnerId)
  if (params.masterId) q.set('masterId', params.masterId)
  return downloadFile(`/admin/reports/winloss/export?${q.toString()}`, 'winloss.csv')
}

// Master-level (admin's top report view). Drill a master → agents (winloss?masterId).
export async function apiAdminGetWinLossByMaster(params: {
  from: string; to: string
}): Promise<MasterWinLossReport[]> {
  const q = new URLSearchParams({ from: params.from, to: params.to })
  return request<MasterWinLossReport[]>(`/admin/reports/winloss/masters?${q.toString()}`)
}

export async function apiAdminExportWinLossByMaster(params: {
  from: string; to: string
}): Promise<void> {
  const q = new URLSearchParams({ from: params.from, to: params.to })
  return downloadFile(`/admin/reports/winloss/masters/export?${q.toString()}`, 'winloss-masters.csv')
}

export async function apiAdminGetAgentBetReport(partnerId: string, params: {
  from: string; to: string; page?: number; limit?: number
}): Promise<PaginatedResult<MemberBetSummary>> {
  const q = new URLSearchParams({ partnerId, from: params.from, to: params.to, page: String(params.page ?? 1), limit: String(params.limit ?? 20) })
  return request<PaginatedResult<MemberBetSummary>>(`/admin/reports/bets?${q.toString()}`)
}

export async function apiAdminGetAgentMemberBets(partnerId: string, memberId: string, params: {
  from: string; to: string; page?: number; limit?: number
}): Promise<PaginatedResult<AgentBetItem>> {
  const q = new URLSearchParams({ partnerId, from: params.from, to: params.to, page: String(params.page ?? 1), limit: String(params.limit ?? 50) })
  return request<PaginatedResult<AgentBetItem>>(`/admin/reports/bets/member/${memberId}?${q.toString()}`)
}

// ─── Admin — Audit Logs ───────────────────────────────────────────────────────

export interface AdminAuditLog {
  id: string
  actorId: string | null
  actorUsername: string | null
  actorType: string | null
  action: string
  entityType: string | null
  entityId: string | null
  oldValue: unknown
  newValue: unknown
  ipAddress: string | null
  requestId: string | null
  createdAt: string
}

export async function apiAdminGetAuditLogs(
  page = 1,
  limit = 50,
  opts: { from?: string; to?: string } = {},
): Promise<PaginatedResult<AdminAuditLog>> {
  const q = new URLSearchParams({ page: String(page), limit: String(limit) })
  if (opts.from) q.set('from', opts.from)
  if (opts.to) q.set('to', opts.to)
  return request<PaginatedResult<AdminAuditLog>>(`/admin/audit-logs?${q.toString()}`)
}

// ─── ประวัติการอั้นเลข ─────────────────────────────────────────────────────────

/** Build the shared query string for both the admin view and the agent's own. */
function numberLimitHistoryQuery(q: NumberLimitHistoryQuery): string {
  const p = new URLSearchParams({
    page: String(q.page ?? 1),
    limit: String(q.limit ?? 50),
  })
  if (q.from) p.set('from', q.from)
  if (q.to) p.set('to', q.to)
  if (q.actions?.length) p.set('actions', q.actions.join(','))
  if (q.scope) p.set('scope', q.scope)
  if (q.lotteryId) p.set('lotteryId', q.lotteryId)
  if (q.number) p.set('number', q.number)
  if (q.partnerId) p.set('partnerId', q.partnerId)
  if (q.actorId) p.set('actorId', q.actorId)
  return p.toString()
}

/** Who to offer in the ตั้งโดย picker — only accounts that actually appear. */
export async function apiAdminGetNumberLimitHistoryActors(): Promise<NumberLimitHistoryActors> {
  return request<NumberLimitHistoryActors>('/admin/number-limit-history/actors')
}

/** Every number limit the house or any agent changed. */
export async function apiAdminGetNumberLimitHistory(
  q: NumberLimitHistoryQuery = {},
): Promise<PaginatedResult<NumberLimitHistoryEntry>> {
  return request<PaginatedResult<NumberLimitHistoryEntry>>(
    `/admin/number-limit-history?${numberLimitHistoryQuery(q)}`,
  )
}

/**
 * This agent's own rules only. partnerId and scope are set from the session server-side,
 * so anything this sends for them is overwritten rather than honoured.
 */
export async function apiAgentGetNumberLimitHistory(
  q: NumberLimitHistoryQuery = {},
): Promise<PaginatedResult<NumberLimitHistoryEntry>> {
  return request<PaginatedResult<NumberLimitHistoryEntry>>(
    `/agent/number-rules/history?${numberLimitHistoryQuery(q)}`,
  )
}

// ─── Admin — Providers ────────────────────────────────────────────────────────

export interface AdminProviderSymbol {
  id: string; symbol: string; displayName: string | null; decimalPlaces: number; isActive: boolean
}

export interface AdminProvider {
  id: string; code: string; name: string; type: string; config: Record<string, unknown>
  isActive: boolean; symbols: AdminProviderSymbol[]; createdAt: string; updatedAt: string
}

export async function apiAdminGetProviders(): Promise<AdminProvider[]> {
  return request<AdminProvider[]>('/admin/providers')
}

export async function apiAdminUpdateProvider(id: string, isActive: boolean): Promise<AdminProvider> {
  return request<AdminProvider>(`/admin/providers/${id}`, { method: 'PUT', body: JSON.stringify({ isActive }) })
}

// ─── Admin — Settlements ──────────────────────────────────────────────────────

export async function apiAdminGetSettlements(page = 1, limit = 20): Promise<PaginatedResult<Settlement>> {
  return request<PaginatedResult<Settlement>>(`/admin/settlements?page=${page}&limit=${limit}`)
}

// ─── Admin — money owed to seamless partners (partner_wallet_outbox) ──────────

export type PartnerWalletFilter = 'attention' | 'failed' | 'retrying' | 'all'

export interface PartnerWalletInstruction {
  id: string
  kind: 'credit' | 'rollback' | 'settle'
  status: 'pending' | 'done' | 'failed' | 'cancelled'
  amount: string
  winAmount: string | null
  result: 'won' | 'lost' | null
  attempts: number
  requeuedAtAttempts: number
  lastError: string | null
  txId: string
  originalTxId: string
  createdAt: string
  nextAttemptAt: string
  deliveredAt: string | null
  partnerName: string
  memberUsername: string
  externalMemberId: string | null
  productCode: string | null
  productName: string | null
  reference: { type: 'draw' | 'round'; id: string; number: string | null }
}

export interface PartnerWalletList {
  items: PartnerWalletInstruction[]
  summary: { failed: number; retrying: number }
}

export async function apiAdminGetPartnerWallet(status: PartnerWalletFilter): Promise<PartnerWalletList> {
  return request<PartnerWalletList>(`/admin/partner-wallet/instructions?status=${status}`)
}

/** Send a failed instruction again. Behind a Telegram code. */
export async function apiAdminRetryPartnerWallet(id: string, otp: OtpConfirmation): Promise<void> {
  await request<{ ok: true }>(`/admin/partner-wallet/instructions/${id}/retry`, {
    method: 'POST', body: JSON.stringify({ otp }),
  })
}

/** Close an instruction that was settled with the partner by hand. Behind a Telegram code. */
export async function apiAdminResolvePartnerWallet(id: string, note: string, otp: OtpConfirmation): Promise<void> {
  await request<{ ok: true }>(`/admin/partner-wallet/instructions/${id}/resolve`, {
    method: 'POST', body: JSON.stringify({ note, otp }),
  })
}

// ─── KENO bet limits ────────────────────────────────────────────────────────────

export interface KenoBetLimit {
  id: string
  min: number
  max: number
  /** false = switched off (ระงับใช้งาน): offered to nobody. */
  isActive: boolean
  sortOrder: number
}

/** One partner's choice among the limits its parent allows. */
export interface KenoPartnerLimits {
  /** What the parent allows — the only rows that can be ticked. */
  options: KenoBetLimit[]
  /** What was ticked (empty = nothing chosen: every allowed row applies). */
  chosen: string[]
  /** What its members can bet at today. */
  effective: KenoBetLimit[]
  /** A choice exists but leaves members nothing to bet at. */
  blocked: boolean
}

export async function apiAdminGetKenoLimits(): Promise<KenoBetLimit[]> {
  return (await request<{ limits: KenoBetLimit[] }>('/admin/keno-limits')).limits
}

export async function apiAdminCreateKenoLimit(body: { min: number; max: number }): Promise<KenoBetLimit> {
  return request<KenoBetLimit>('/admin/keno-limits', { method: 'POST', body: JSON.stringify(body) })
}

export async function apiAdminUpdateKenoLimit(
  id: string,
  patch: { min?: number; max?: number; isActive?: boolean },
): Promise<KenoBetLimit> {
  return request<KenoBetLimit>(`/admin/keno-limits/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
}

export async function apiAdminGetPartnerKenoLimits(partnerId: string): Promise<KenoPartnerLimits> {
  return request<KenoPartnerLimits>(`/admin/keno-limits/partners/${partnerId}`)
}

export async function apiAdminPutPartnerKenoLimits(partnerId: string, limitIds: string[]): Promise<KenoPartnerLimits> {
  return request<KenoPartnerLimits>(`/admin/keno-limits/partners/${partnerId}`, {
    method: 'PUT', body: JSON.stringify({ limitIds }),
  })
}

export async function apiMasterGetAgentKenoLimits(agentId: string): Promise<KenoPartnerLimits> {
  return request<KenoPartnerLimits>(`/agent/agents/${agentId}/keno-limits`)
}

export async function apiMasterPutAgentKenoLimits(agentId: string, limitIds: string[]): Promise<KenoPartnerLimits> {
  return request<KenoPartnerLimits>(`/agent/agents/${agentId}/keno-limits`, {
    method: 'PUT', body: JSON.stringify({ limitIds }),
  })
}

// ─── Admin — Master tier ────────────────────────────────────────────────────────
export interface AdminMaster extends AdminPartner {
  agentCount: number
  /** Whether this whole line is the testing line — set here, inherited by its agents. */
  isStaging: boolean
  // the highest agent share already handed out — the cap can't be lowered below this
  maxAgentShare: string
}

export interface CreateMasterBody {
  code: string
  name: string
  cap: string // rate 0..1
  username: string
  password: string
}

export async function apiAdminGetMasters(): Promise<AdminMaster[]> {
  return request<AdminMaster[]>('/admin/masters')
}

export async function apiAdminCreateMaster(body: CreateMasterBody): Promise<AdminMaster> {
  return request<AdminMaster>('/admin/masters', { method: 'POST', body: JSON.stringify(body) })
}

export async function apiAdminUpdateMasterCap(id: string, cap: string): Promise<AdminPartner> {
  return request<AdminPartner>(`/admin/masters/${id}/cap`, {
    method: 'PATCH',
    body: JSON.stringify({ cap }),
  })
}

export async function apiAdminSetMasterStatus(
  id: string,
  status: 'active' | 'suspended',
): Promise<AdminPartner> {
  return request<AdminPartner>(`/admin/masters/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  })
}

// ─── Admin — accounts + per-menu permissions ───────────────────────────────────
// Menu keys a limited admin can be granted. Super admin (permissions=null) has all.
export const ADMIN_MENU_KEYS = [
  'rounds', 'bets', 'games', 'draws', 'payout-rates', 'number-limits',
  'settlements', 'reports', 'masters', 'providers', 'audit-logs', 'settings', 'staging',
] as const
export type AdminMenuKey = (typeof ADMIN_MENU_KEYS)[number]

// ─── Admin — settings (Telegram notification group) ────────────────────────────
export interface TelegramSettingsMasked {
  enabled: boolean
  chatId: string
  hasToken: boolean
  tokenPreview: string
}

export interface UpdateTelegramBody {
  enabled: boolean
  chatId: string
  /** Omit to keep the stored token; the server never sends the token back. */
  botToken?: string
}

export async function apiAdminGetTelegramSettings(): Promise<TelegramSettingsMasked> {
  return request<TelegramSettingsMasked>('/admin/settings/telegram')
}

export async function apiAdminUpdateTelegramSettings(
  body: UpdateTelegramBody,
  otp: OtpConfirmation,
): Promise<TelegramSettingsMasked> {
  return request<TelegramSettingsMasked>('/admin/settings/telegram', {
    method: 'PUT',
    body: JSON.stringify({ ...body, otp }),
  })
}

export async function apiAdminTestTelegram(): Promise<{ ok: boolean }> {
  return request('/admin/settings/telegram/test', { method: 'POST' })
}

/**
 * Whether the worker opens the next งวด from each lottery's schedule by itself, and how
 * long it waits after an announcement before doing so. Re-exported from the shared types
 * rather than restated here — a second copy is how the two ends drift apart unnoticed.
 */
export type { DrawAutoOpenSettings }

export async function apiAdminGetDrawAutoOpen(): Promise<DrawAutoOpenSettings> {
  return request<DrawAutoOpenSettings>('/admin/settings/draw-auto-open')
}

export async function apiAdminSetDrawAutoOpen(
  settings: DrawAutoOpenSettings,
): Promise<DrawAutoOpenSettings> {
  return request<DrawAutoOpenSettings>('/admin/settings/draw-auto-open', {
    method: 'PUT',
    body: JSON.stringify(settings),
  })
}

// ─── Partner API IP whitelist ─────────────────────────────────────────────────

export interface IpWhitelistEntry {
  id: string
  ipAddress: string
  /** null = applies to every agent. */
  partnerId: string | null
  partnerName: string | null
  partnerCode: string | null
  note: string | null
  createdByUsername: string | null
  createdAt: string
}

export interface IpWhitelistInput {
  ipAddress: string
  partnerId?: string | null
  note?: string | null
}

export interface IpWhitelistAgent {
  id: string
  code: string
  name: string
  level: string
}

/** Entries plus the agents the dropdown can point at — one call, one permission gate. */
export async function apiAdminGetIpWhitelist(): Promise<{ entries: IpWhitelistEntry[]; agents: IpWhitelistAgent[] }> {
  return request<{ entries: IpWhitelistEntry[]; agents: IpWhitelistAgent[] }>('/admin/settings/ip-whitelist')
}

export async function apiAdminCreateIpWhitelist(
  body: IpWhitelistInput, otp: OtpConfirmation,
): Promise<IpWhitelistEntry> {
  return request<IpWhitelistEntry>('/admin/settings/ip-whitelist', {
    method: 'POST', body: JSON.stringify({ ...body, otp }),
  })
}

export async function apiAdminDeleteIpWhitelist(id: string, otp: OtpConfirmation): Promise<void> {
  return request<void>(`/admin/settings/ip-whitelist/${id}`, {
    method: 'DELETE', body: JSON.stringify({ otp }),
  })
}

// ─── Home-page banners ────────────────────────────────────────────────────────

export async function apiAdminGetBanners(): Promise<Banner[]> {
  return request<Banner[]>('/admin/settings/banners')
}

export async function apiAdminCreateBanner(body: BannerInput): Promise<Banner> {
  return request<Banner>('/admin/settings/banners', { method: 'POST', body: JSON.stringify(body) })
}

export async function apiAdminUpdateBanner(id: string, body: BannerInput): Promise<Banner> {
  return request<Banner>(`/admin/settings/banners/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
}

export async function apiAdminDeleteBanner(id: string): Promise<void> {
  return request<void>(`/admin/settings/banners/${id}`, { method: 'DELETE' })
}

/** Banner image URLs come back relative to the API root — prefix them for this app. */
export function bannerImageUrl(banner: Banner): string {
  return `${API_BASE}${banner.imageUrl}`
}

export interface AdminAccount {
  id: string
  username: string
  displayName: string | null
  status: string
  permissions: string[] | null // null = super admin (all menus)
  createdAt: string
}

export interface CreateAdminBody {
  username: string
  password: string
  displayName?: string
  permissions: string[] | null
}

export async function apiAdminGetAdmins(): Promise<AdminAccount[]> {
  return request<AdminAccount[]>('/admin/admins')
}

export async function apiAdminCreateAdmin(
  body: CreateAdminBody, otp: OtpConfirmation,
): Promise<AdminAccount> {
  return request<AdminAccount>('/admin/admins', {
    method: 'POST', body: JSON.stringify({ ...body, otp }),
  })
}

export async function apiAdminSetAdminStatus(
  id: string,
  status: 'active' | 'suspended',
  otp: OtpConfirmation,
): Promise<{ ok: boolean; status: string }> {
  return request(`/admin/admins/${id}/status`, {
    method: 'PATCH', body: JSON.stringify({ status, otp }),
  })
}

export async function apiAdminUpdateAdminPermissions(
  id: string,
  permissions: string[] | null,
  otp: OtpConfirmation,
): Promise<{ ok: boolean }> {
  return request(`/admin/admins/${id}/permissions`, {
    method: 'PATCH',
    body: JSON.stringify({ permissions, otp }),
  })
}

// Admin drills read-only: a master's agents, then an agent's members.
export async function apiAdminGetMasterAgents(masterId: string): Promise<AdminPartner[]> {
  return request<AdminPartner[]>(`/admin/masters/${masterId}/agents`)
}

export async function apiAdminGetAgentMembers(
  agentId: string,
  page = 1,
  limit = 20,
): Promise<PaginatedResult<AdminMember>> {
  return request<PaginatedResult<AdminMember>>(
    `/admin/agents/${agentId}/members?page=${page}&limit=${limit}`,
  )
}

// ─── Agent ────────────────────────────────────────────────────────────────────

export async function apiAgentGetProfile(): Promise<AdminPartner> {
  return request<AdminPartner>('/agent/profile')
}

export async function apiAgentGetDashboard(): Promise<AdminDashboardStats> {
  return request<AdminDashboardStats>('/agent/dashboard')
}

export async function apiAgentGetMembers(page = 1, limit = 20): Promise<PaginatedResult<AdminMember>> {
  return request<PaginatedResult<AdminMember>>(`/agent/members?page=${page}&limit=${limit}`)
}

// ─── Master tier: manage sub-agents ─────────────────────────────────────────────
export async function apiAgentGetAgents(): Promise<AdminPartner[]> {
  return request<AdminPartner[]>('/agent/agents')
}

export interface CreateAgentBody {
  code: string
  name: string
  // agent's own share; the server derives master_share = cap − this
  commissionRate: string
  walletType: 'internal' | 'seamless'
  walletUrl?: string
  walletApiKey?: string
  // the agent's own login for the agent console — required at creation
  username: string
  password: string
}

export async function apiAgentCreateAgent(
  body: CreateAgentBody,
): Promise<AdminPartner & { nexbitApiKey: string; launchSecret: string }> {
  return request<AdminPartner & { nexbitApiKey: string; launchSecret: string }>('/agent/agents', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function apiAgentImpersonate(memberId: string): Promise<{ token: string; expiresIn: number }> {
  return request<{ token: string; expiresIn: number }>(
    `/agent/members/${memberId}/impersonate`,
    { method: 'POST' },
  )
}

// ─── Master tier: drill into one agent (read-only members + edit + status) ──────
export interface UpdateAgentBody {
  name: string
  // agent's own share; the server derives master_share = cap − this
  commissionRate: string
  walletType: 'internal' | 'seamless'
  walletUrl?: string
  walletApiKey?: string
}

export async function apiMasterGetAgent(agentId: string): Promise<AdminPartner> {
  return request<AdminPartner>(`/agent/agents/${agentId}`)
}

export async function apiMasterGetAgentMembers(
  agentId: string,
  page = 1,
  limit = 20,
): Promise<PaginatedResult<AdminMember>> {
  return request<PaginatedResult<AdminMember>>(
    `/agent/agents/${agentId}/members?page=${page}&limit=${limit}`,
  )
}

export async function apiMasterUpdateAgent(agentId: string, body: UpdateAgentBody): Promise<AdminPartner> {
  return request<AdminPartner>(`/agent/agents/${agentId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function apiMasterSetAgentStatus(
  agentId: string,
  status: 'active' | 'suspended',
): Promise<AdminPartner> {
  return request<AdminPartner>(`/agent/agents/${agentId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  })
}

/** Master sets a new password on one of its agents' login. The agent's saved session is revoked. */
export async function apiMasterResetAgentPassword(
  agentId: string,
  newPassword: string,
): Promise<{ ok: boolean; username: string }> {
  return request<{ ok: boolean; username: string }>(`/agent/agents/${agentId}/password`, {
    method: 'PATCH',
    body: JSON.stringify({ newPassword }),
  })
}

export async function apiAgentGetWinLossReport(params: { from: string; to: string }): Promise<WinLossReport[]> {
  const q = new URLSearchParams({ from: params.from, to: params.to })
  return request<WinLossReport[]>(`/agent/reports/winloss?${q.toString()}`)
}

export async function apiAgentExportWinLossReport(params: { from: string; to: string }): Promise<void> {
  const q = new URLSearchParams({ from: params.from, to: params.to })
  return downloadFile(`/agent/reports/winloss/export?${q.toString()}`, 'winloss.csv')
}

export async function apiAgentGetBetReport(params: {
  from: string; to: string; page?: number; limit?: number
}): Promise<PaginatedResult<MemberBetSummary>> {
  const q = new URLSearchParams({ from: params.from, to: params.to, page: String(params.page ?? 1), limit: String(params.limit ?? 20) })
  return request<PaginatedResult<MemberBetSummary>>(`/agent/reports/bets?${q.toString()}`)
}

export async function apiAgentExportBetReport(params: { from: string; to: string }): Promise<void> {
  const q = new URLSearchParams({ from: params.from, to: params.to })
  return downloadFile(`/agent/reports/bets/export?${q.toString()}`, 'member-report.csv')
}

export async function apiAgentGetMemberBets(memberId: string, params: {
  from: string; to: string; page?: number; limit?: number
}): Promise<PaginatedResult<AgentBetItem>> {
  const q = new URLSearchParams({ from: params.from, to: params.to, page: String(params.page ?? 1), limit: String(params.limit ?? 50) })
  return request<PaginatedResult<AgentBetItem>>(`/agent/reports/bets/member/${memberId}?${q.toString()}`)
}

export async function apiAgentUpdateMemberStatus(memberId: string, status: 'active' | 'suspended'): Promise<{ ok: boolean; status: string }> {
  return request<{ ok: boolean; status: string }>(`/agent/members/${memberId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  })
}

export async function apiAgentUpdateSettings(maintenanceMode: boolean): Promise<{ ok: boolean; maintenanceMode: boolean }> {
  return request<{ ok: boolean; maintenanceMode: boolean }>('/agent/settings', {
    method: 'PATCH',
    body: JSON.stringify({ maintenanceMode }),
  })
}

/** The agent's own secret — what THEIR wallet server checks Nexbit's requests against. */
export async function apiAgentSetWalletApiKey(walletApiKey: string): Promise<{ ok: boolean; walletApiKeyMasked: string }> {
  return request<{ ok: boolean; walletApiKeyMasked: string }>('/agent/settings/wallet-api-key', {
    method: 'PATCH',
    body: JSON.stringify({ walletApiKey }),
  })
}

export async function apiAgentRotateNexbitApiKey(): Promise<{ nexbitApiKey: string }> {
  return request<{ nexbitApiKey: string }>('/agent/keys/rotate-nexbit-api-key', { method: 'POST' })
}

export async function apiAgentRotateLaunchSecret(): Promise<{ launchSecret: string }> {
  return request<{ launchSecret: string }>('/agent/keys/rotate-launch-secret', { method: 'POST' })
}

export async function apiAgentChangePassword(currentPassword: string, newPassword: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/agent/change-password', {
    method: 'PATCH',
    body: JSON.stringify({ currentPassword, newPassword }),
  })
}

// ─── Admin — Draw Lotteries ─────────────────────────────────────────────────────

export interface DrawResultBody {
  prizeFirst: string
  /** null when the lottery announces no 2 ตัวล่าง (no bottom bet type is enabled). */
  bottomTwo: string | null
  /** เลขหน้า 3 ตัว (two numbers) — sent only when the lottery offers 3front. */
  frontThree?: string[] | null
  /** เลขท้าย 3 ตัว (two numbers) — sent only when the lottery offers 3bottom. */
  bottomThree?: string[] | null
}

/** Phase-1 / edit acknowledgement — the draw is now (still) in the `resulted` state. */
export interface DrawResultAck {
  status: 'resulted'
}

/** Phase-2 settle outcome — how many bets were paid out. */
export interface DrawSettleSummary {
  settled: number
  won: number
  lost: number
}

/** Void outcome — payouts clawed back from winners. */
export interface DrawVoidSummary {
  reversed: number
  totalReturned: string
}

/** Cancel outcome — every bet on the draw refunded. */
export interface DrawCancelSummary {
  refunded: number
  totalRefunded: string
}

/**
 * Lotteries with the งวด in play, ordered by how soon it is announced. Includes the
 * switched-off ones: a lottery hidden from the lobby has to stay reachable here, or
 * it could only ever be switched back on by editing the database.
 */
export async function apiDrawGetLotteries(): Promise<DrawLotteryWithCurrent[]> {
  return request<DrawLotteryWithCurrent[]>('/draws/lotteries?includeInactive=true')
}

/** Show/hide a lottery for members. */
export async function apiDrawSetActive(
  lotteryId: string,
  isActive: boolean,
): Promise<{ isActive: boolean }> {
  return request<{ isActive: boolean }>(`/draws/lotteries/${lotteryId}/active`, {
    method: 'PATCH',
    body: JSON.stringify({ isActive }),
  })
}

export async function apiDrawGetDraws(
  lotteryId: string,
): Promise<{ draws: DrawWithResult[]; openDraw: Draw | null }> {
  return request<{ draws: DrawWithResult[]; openDraw: Draw | null }>(
    `/draws/lotteries/${lotteryId}/draws`,
  )
}

export interface DrawSlot {
  drawAt: string
  openAt: string
  closeAt: string
  /** Which round of the day the slot is — 0 on a lottery that announces once. */
  roundIndex: number
  /** The round's name, when the schedule gives it one. */
  roundLabel: string | null
}

/** Times an admin types by hand, when the schedule is not deciding them. */
export type CreateDrawTimes = Pick<DrawSlot, 'drawAt' | 'openAt' | 'closeAt'>

/** What this lottery's schedule points at next (null for a manual schedule). */
export async function apiDrawNextSlot(lotteryId: string): Promise<{ slot: DrawSlot | null }> {
  return request<{ slot: DrawSlot | null }>(`/draws/lotteries/${lotteryId}/next-slot`)
}

/**
 * Update payout rates and stake limits. Re-authenticates with the admin's own
 * password — the server rejects the call if it does not match, and the endpoint is
 * rate-limited, so a wrong password is not something to retry in a loop.
 */
export async function apiDrawUpdatePayout(
  lotteryId: string,
  body: { password: string; betConfig: Partial<Record<DrawBetType, DrawBetTypeConfig>> },
  otp: OtpConfirmation,
): Promise<DrawLottery> {
  return request<DrawLottery>(`/draws/lotteries/${lotteryId}/payout`, {
    method: 'PATCH',
    body: JSON.stringify({ ...body, otp }),
  })
}

/**
 * Whether the server refused a request because it needed a code that was not sent.
 *
 * The screens work out for themselves whether a change raises a ceiling and ask for the
 * code up front, but they are working from the last poll — and the server decides against
 * what it holds right now. When the two disagree this is how the screen finds out, and it
 * can open the code dialog instead of leaving the operator with a bare error.
 * (The sentence is requireOtp's own, in services/api/src/middleware/requireOtp.ts.)
 */
export function isOtpRequiredError(e: unknown): boolean {
  return e instanceof ApiRequestError && e.message.includes('ต้องยืนยันด้วยรหัสจากกลุ่มเทเลแกรม')
}

/**
 * What a code is bound to: the route's own parameters plus the body the action will be
 * submitted with. Both halves are fingerprinted server-side, so a code issued for one
 * งวด cannot be typed into a request against another, and a code issued to set the
 * Telegram group to X cannot be used to set it to Y.
 *
 * It has to match exactly what the server rebuilds from the real request, which is why
 * callers should build it once and pass the same object to both calls rather than
 * writing it out twice.
 */
export interface OtpPayload {
  params?: Record<string, string>
  body?: Record<string, unknown>
}

/**
 * Ask for the code that authorises one specific admin action. It goes to the operators'
 * Telegram group — never back here — so completing the action means reading it there,
 * which is the whole point: a stolen session cannot finish on its own.
 */
export async function apiAdminRequestOtp(
  purpose: string,
  payload: OtpPayload,
): Promise<OtpChallengeIssued> {
  return request<OtpChallengeIssued>('/admin/otp', {
    method: 'POST',
    body: JSON.stringify({ purpose, payload }),
  })
}

/**
 * Ask for the code that authorises creating a lottery. It is sent to the operators'
 * Telegram group, never returned here — completing the action means reading it there.
 */
export async function apiDrawRequestLotteryOtp(
  draft: CreateDrawLotteryRequest,
): Promise<OtpChallengeIssued> {
  return request<OtpChallengeIssued>('/draws/lotteries/otp', {
    method: 'POST',
    body: JSON.stringify(draft),
  })
}

/**
 * Create the lottery. The draft must be byte-for-byte what the code was issued for —
 * the server checks it against a fingerprint taken when the group was told.
 */
export async function apiDrawCreateLottery(
  draft: CreateDrawLotteryRequest,
  confirmation: OtpConfirmation,
): Promise<DrawLottery> {
  return request<DrawLottery>('/draws/lotteries', {
    method: 'POST',
    body: JSON.stringify({ ...draft, otp: confirmation }),
  })
}

/** Update a lottery's timetable and betting window. */
export async function apiDrawUpdateSettings(
  lotteryId: string,
  body: {
    drawSchedule?: DrawScheduleConfig
    betCloseBeforeSeconds?: number
    /** null = back to opening when the งวด is created. Omit to leave it unchanged. */
    betOpenBeforeSeconds?: number | null
    /** null = never use this rule. Omit to leave it unchanged. */
    betOpenAfterPreviousSeconds?: number | null
  },
): Promise<DrawLottery> {
  return request<DrawLottery>(`/draws/lotteries/${lotteryId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

/**
 * Open the next งวด (allowed once the previous one is settled). Omit `body` to let
 * the lottery's schedule decide the times.
 */
export async function apiDrawCreate(lotteryId: string, body?: CreateDrawTimes): Promise<Draw> {
  return request<Draw>(`/draws/lotteries/${lotteryId}/draws`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  })
}

/** Open/close the market for a lottery — blocks betting without touching any draw. */
export async function apiDrawSetMarket(
  lotteryId: string,
  isMarketOpen: boolean,
): Promise<{ isMarketOpen: boolean }> {
  return request<{ isMarketOpen: boolean }>(`/draws/lotteries/${lotteryId}/market`, {
    method: 'PATCH',
    body: JSON.stringify({ isMarketOpen }),
  })
}

/** Numbers currently refused on a lottery (อั้นเลข). drawId null = every draw. */
export async function apiDrawBlockedNumbers(
  lotteryId: string,
  drawId?: string,
): Promise<DrawBlockedNumber[]> {
  const q = drawId ? `?drawId=${drawId}` : ''
  return request<DrawBlockedNumber[]>(`/draws/lotteries/${lotteryId}/blocked${q}`)
}

export async function apiDrawBlockNumber(
  lotteryId: string,
  body: { betType: DrawBetType; number: string; drawId?: string | null; note?: string | null },
): Promise<DrawBlockedNumber> {
  return request<DrawBlockedNumber>(`/draws/lotteries/${lotteryId}/blocked`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function apiDrawUnblockNumber(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/draws/blocked/${id}`, { method: 'DELETE' })
}

export interface DrawExposure {
  drawId: string
  drawNumber: number
  status: DrawStatus
  betConfig: Partial<Record<DrawBetType, DrawBetTypeConfig>>
  byType: { betType: DrawBetType; stake: string; bets: number }[]
  byNumber: { betType: DrawBetType; number: string; stake: string; bets: number }[]
}

/**
 * Stake taken on a draw so far, per bet type and per number, with the caps.
 * Pass betType to get every number of that type; without it the number list is the
 * top 100 across all types.
 */
export async function apiDrawExposure(drawId: string, betType?: DrawBetType): Promise<DrawExposure> {
  const q = betType ? `?betType=${betType}` : ''
  return request<DrawExposure>(`/draws/${drawId}/exposure${q}`)
}

// ─── เพดานรายเลขของเว็บ — a number's own ceiling, overriding its bet type's default ───

/** Every override on a lottery, all bet types. */
export async function apiDrawNumberCaps(lotteryId: string): Promise<DrawNumberCap[]> {
  return request<DrawNumberCap[]>(`/draws/lotteries/${lotteryId}/number-caps`)
}

/**
 * Set (or restate) a batch of overrides. An existing one on the same number is replaced.
 *
 * `otp` is needed only when the batch RAISES a ceiling — the server decides, against what
 * it holds, and refuses a raise that arrives without one. Lowering needs none.
 */
export async function apiDrawSetNumberCaps(
  lotteryId: string,
  caps: DrawNumberCapInput[],
  otp?: OtpConfirmation,
): Promise<{ caps: DrawNumberCap[] }> {
  return request<{ caps: DrawNumberCap[] }>(`/draws/lotteries/${lotteryId}/number-caps`, {
    method: 'PUT',
    body: JSON.stringify(otp ? { caps, otp } : { caps }),
  })
}

/**
 * Put one number back on its bet type's default. That is a RAISE when the override was
 * lower than the default, and then it needs `otp`; removing a raised one needs none.
 */
export async function apiDrawDeleteNumberCap(id: string, otp?: OtpConfirmation): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/draws/number-caps/${id}`, {
    method: 'DELETE',
    ...(otp ? { body: JSON.stringify({ otp }) } : {}),
  })
}

// ─── อั้นเลขของเอเย่นต์ — rules an agent sets for its own members ────────────────

/**
 * An agent's rules plus the house values they are measured against, in one call.
 *
 * The house config travels with them so the screen can show "ตั้งไว้ 850 · มีผลจริง 800"
 * without fetching and reconciling two sources — an agent whose rule is capped by the
 * house has to see both numbers, or they believe they are stricter than they are.
 */
export interface AgentNumberRulesResponse {
  rules: AgentNumberRule[]
  houseConfig: Partial<Record<DrawBetType, DrawBetTypeConfig>>
  /**
   * The house's own ceiling on individual numbers, where it differs from the bet type's
   * default. Optional: an API older than this field has none, and every number then uses
   * `houseConfig[betType].maxPerNumber` as it always did.
   */
  houseNumberCaps?: { betType: DrawBetType; number: string; maxPerNumber: string }[]
}

export async function apiAgentNumberRules(
  lotteryId: string,
  drawId?: string | null,
): Promise<AgentNumberRulesResponse> {
  const q = drawId ? `&drawId=${drawId}` : ''
  return request<AgentNumberRulesResponse>(`/agent/number-rules?lotteryId=${lotteryId}${q}`)
}

/**
 * Write a batch of rules. Always an array and always an upsert: agents key เลขอั้น in
 * sets, and re-stating a ceiling must change it rather than fail as a duplicate.
 */
export async function apiAgentSaveNumberRules(body: {
  lotteryId: string
  drawId?: string | null
  rules: AgentNumberRuleInput[]
}): Promise<{ rules: AgentNumberRule[] }> {
  return request<{ rules: AgentNumberRule[] }>('/agent/number-rules', {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function apiAgentDeleteNumberRule(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/agent/number-rules/${id}`, { method: 'DELETE' })
}

/**
 * Clear every rule this agent has on one lottery — all bet types, whole-type rules
 * included. Rules carry over from งวด to งวด, so this is the only way to start over.
 */
export async function apiAgentClearNumberRules(lotteryId: string): Promise<{ cleared: number }> {
  return request<{ cleared: number }>(`/agent/number-rules?lotteryId=${lotteryId}`, { method: 'DELETE' })
}

/** What this agent is carrying on a งวด, alongside what the house is carrying. */
export interface AgentExposure {
  drawId: string
  drawNumber: number
  status: DrawStatus
  mine: Pick<DrawExposure, 'byType' | 'byNumber'>
  house: Pick<DrawExposure, 'byType' | 'byNumber'>
}

export async function apiAgentExposure(
  drawId: string,
  betType?: DrawBetType,
): Promise<AgentExposure> {
  const q = betType ? `?betType=${betType}` : ''
  return request<AgentExposure>(`/agent/draws/${drawId}/exposure${q}`)
}

/**
 * Intake caps for one bet type. No password prompt. A code from the Telegram group is
 * needed only when the change RAISES any of the three — the server decides, and refuses a
 * raise that arrives without one. Lowering them, which only restricts intake, needs none.
 */
export async function apiDrawUpdateLimits(
  lotteryId: string,
  body: { betType: DrawBetType; maxBet: number; maxPerNumber: number; maxPerType: number },
  otp?: OtpConfirmation,
): Promise<DrawLottery> {
  return request<DrawLottery>(`/draws/lotteries/${lotteryId}/limits`, {
    method: 'PATCH',
    body: JSON.stringify(otp ? { ...body, otp } : body),
  })
}

/** Stop taking bets right now, without waiting for close_at (scheduled/open → closed). */
export async function apiDrawClose(drawId: string): Promise<{ status: 'closed' }> {
  return request<{ status: 'closed' }>(`/draws/${drawId}/close`, { method: 'POST' })
}

/** Undo an early close — only while the draw has no result entered yet. */
export async function apiDrawReopen(drawId: string): Promise<{ status: 'open'; closeAt: string }> {
  return request<{ status: 'open'; closeAt: string }>(`/draws/${drawId}/reopen`, { method: 'POST' })
}

/** PHASE 1 — record the announced result only (NO payout). Closes betting first if still open. */
export async function apiDrawEnterResult(
  drawId: string,
  body: DrawResultBody,
): Promise<DrawResultAck> {
  return request<DrawResultAck>(`/draws/${drawId}/result`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** Correct the announced result while still `resulted` (before settle). */
export async function apiDrawUpdateResult(
  drawId: string,
  body: DrawResultBody,
): Promise<DrawResultAck> {
  return request<DrawResultAck>(`/draws/${drawId}/result`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/** PHASE 2 — pay out winners. Draw must be `resulted`. */
export async function apiDrawSettle(drawId: string): Promise<DrawSettleSummary> {
  return request<DrawSettleSummary>(`/draws/${drawId}/settle`, { method: 'POST' })
}

/** Reverse a `settled` draw (claw back payouts) → returns it to `resulted`. */
export async function apiDrawVoid(drawId: string, otp: OtpConfirmation): Promise<DrawVoidSummary> {
  return request<DrawVoidSummary>(`/draws/${drawId}/void`, {
    method: 'POST',
    body: JSON.stringify({ otp }),
  })
}

/**
 * Cancel a draw that can never be settled and refund every bet on it. For a งวด whose
 * source never announced a result — not for correcting one that already paid out
 * (that is `apiDrawVoid`).
 */
export async function apiDrawCancel(
  drawId: string,
  reason: string,
  otp: OtpConfirmation,
): Promise<DrawCancelSummary> {
  return request<DrawCancelSummary>(`/draws/${drawId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason, otp }),
  })
}

/**
 * งดออกผล — refunds every bet like a cancellation, but the งวด stays visible to members
 * reading "งดออกผล" and keeps its slot, so the scheduler will not re-open the same round.
 */
export async function apiDrawSkip(
  drawId: string,
  reason: string,
  otp: OtpConfirmation,
): Promise<DrawCancelSummary> {
  return request<DrawCancelSummary>(`/draws/${drawId}/skip`, {
    method: 'POST',
    body: JSON.stringify({ reason, otp }),
  })
}

// ─── Admin — staging integration tester ────────────────────────────────────────

/**
 * Fires the calls a real round would make at an agent's own endpoints and reports what
 * came back, so an operator can prove an integration works without a developer. Only
 * partners in the staging line can be targeted — the wallet cases move real money on
 * the agent's side.
 */

export async function apiAdminStagingTargets(): Promise<StagingTarget[]> {
  return request<StagingTarget[]>('/admin/staging/targets')
}

export async function apiAdminStagingCases(): Promise<StagingCaseMeta[]> {
  return request<StagingCaseMeta[]>('/admin/staging/cases')
}

/** Starts the run and returns immediately — poll `apiAdminStagingRun` for progress. */
export async function apiAdminStagingCreateRun(
  body: CreateStagingRunRequest,
): Promise<{ runId: string }> {
  return request<{ runId: string }>('/admin/staging/runs', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function apiAdminStagingRun(runId: string): Promise<StagingRunDetail> {
  return request<StagingRunDetail>(`/admin/staging/runs/${runId}`)
}

export async function apiAdminStagingRuns(params: {
  partnerId?: string
  status?: string
  from?: string
  to?: string
  page?: number
  limit?: number
}): Promise<PaginatedResult<StagingRunSummary>> {
  const q = new URLSearchParams()
  if (params.partnerId) q.set('partnerId', params.partnerId)
  if (params.status) q.set('status', params.status)
  if (params.from) q.set('from', params.from)
  if (params.to) q.set('to', params.to)
  q.set('page', String(params.page ?? 1))
  q.set('limit', String(params.limit ?? 20))
  return request<PaginatedResult<StagingRunSummary>>(`/admin/staging/runs?${q.toString()}`)
}

export async function apiAdminStagingExportRuns(params: { from?: string; to?: string; partnerId?: string }): Promise<void> {
  const q = new URLSearchParams()
  if (params.from) q.set('from', params.from)
  if (params.to) q.set('to', params.to)
  if (params.partnerId) q.set('partnerId', params.partnerId)
  return downloadFile(`/admin/staging/runs/export?${q.toString()}`, 'staging-runs.csv')
}

/** The report the operator hands the agent. Pass a caseKey for just that one check. */
export async function apiAdminStagingReport(runId: string, caseKey?: string): Promise<{ markdown: string }> {
  const q = caseKey ? `?caseKey=${encodeURIComponent(caseKey)}` : ''
  return request<{ markdown: string }>(`/admin/staging/runs/${runId}/report${q}`)
}

export async function apiAdminStagingDownloadReport(runId: string): Promise<void> {
  return downloadFile(`/admin/staging/runs/${runId}/report/download`, `nexbit-test-${runId.slice(0, 8)}.md`)
}

export async function apiAdminStagingWinLoss(params: { from: string; to: string }): Promise<WinLossReport[]> {
  const q = new URLSearchParams({ from: params.from, to: params.to })
  return request<WinLossReport[]>(`/admin/staging/reports/winloss?${q.toString()}`)
}

export async function apiAdminStagingExportWinLoss(params: { from: string; to: string }): Promise<void> {
  const q = new URLSearchParams({ from: params.from, to: params.to })
  return downloadFile(`/admin/staging/reports/winloss/export?${q.toString()}`, 'staging-winloss.csv')
}

export interface StagingFootprint {
  agentCount: number
  memberCount: number
  recentStake: string
}

/** What a line contains — shown before turning the staging flag on. */
export async function apiAdminStagingFootprint(masterId: string): Promise<StagingFootprint> {
  return request<StagingFootprint>(`/admin/masters/${masterId}/staging-footprint`)
}

export async function apiAdminSetMasterStaging(
  masterId: string,
  isStaging: boolean,
): Promise<{ id: string; isStaging: boolean }> {
  return request<{ id: string; isStaging: boolean }>(`/admin/masters/${masterId}/staging`, {
    method: 'PATCH',
    body: JSON.stringify({ isStaging, confirm: true }),
  })
}

// ─── Draw lottery กติกา ────────────────────────────────────────────────────────

/** The published body for one lottery. `html` is already sanitised by the API. */
export async function apiDrawRules(lotteryId: string): Promise<DrawLotteryRules> {
  return request<DrawLotteryRules>(`/draws/lotteries/${lotteryId}/rules`)
}

/**
 * Replace the body. The API sanitises before storing, so what comes back is what members
 * will actually see — feed that back into the editor rather than the submitted string.
 */
export async function apiDrawSaveRules(lotteryId: string, html: string): Promise<DrawLotteryRules> {
  return request<DrawLotteryRules>(`/draws/lotteries/${lotteryId}/rules`, {
    method: 'PUT',
    body: JSON.stringify({ html }),
  })
}

/** Upload artwork for a lottery's กติกา; returns the path to embed in the body. */
export async function apiDrawUploadRuleImage(
  lotteryId: string,
  imageBase64: string,
): Promise<DrawLotteryRuleImage> {
  return request<DrawLotteryRuleImage>(`/draws/lotteries/${lotteryId}/rule-images`, {
    method: 'POST',
    body: JSON.stringify({ imageBase64 }),
  })
}

/**
 * Rules images come back relative to the API root, and that is how they are stored inside
 * the body — the member app and this one sit on different origins and each prefixes its
 * own base. Rewrite them for display here.
 */
export function ruleImageSrc(path: string): string {
  return `${API_BASE}${path}`
}

/** Point every stored rules image at this app's API origin, for preview and editing. */
export function withRuleImageOrigin(html: string): string {
  return html.split('src="/draw-lotteries/rule-images/')
    .join(`src="${API_BASE}/draw-lotteries/rule-images/`)
}

/** The inverse — strip this app's origin back off before saving. */
export function stripRuleImageOrigin(html: string): string {
  return html.split(`src="${API_BASE}/draw-lotteries/rule-images/`)
    .join('src="/draw-lotteries/rule-images/')
}
